import { useEffect, useState } from 'react';
import { HERO_SLIDES as DEFAULT_SLIDES } from '../data/destinationImages';
import { getHeroSlidesToLoad, preloadImage } from '../utils/imageLoading';

const SLIDE_INTERVAL_MS = 5500;

function HeroSlideshow({
  slides = DEFAULT_SLIDES,
  fallback = null,
  onSlideChange,
  classPrefix = 'home-hero',
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const slidesToLoad = getHeroSlidesToLoad(activeIndex, slides.length);

  useEffect(() => {
    onSlideChange?.(activeIndex);
  }, [activeIndex, onSlideChange]);

  useEffect(() => {
    if (slides[0]?.src) {
      preloadImage(slides[0].src);
    }
  }, [slides]);

  useEffect(() => {
    const nextIndex = (activeIndex + 1) % slides.length;
    const nextSlide = slides[nextIndex];
    if (nextSlide?.src) {
      preloadImage(nextSlide.src);
    }
  }, [activeIndex, slides]);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || slides.length <= 1) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % slides.length);
    }, SLIDE_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [slides.length]);

  const handleError = (event) => {
    if (fallback && event.currentTarget.src !== fallback) {
      event.currentTarget.src = fallback;
    }
  };

  return (
    <div className={`${classPrefix}__slideshow`}>
      {slides.map((slide, index) => {
        const isActive = index === activeIndex;
        const shouldLoad = slidesToLoad.has(index);

        return (
          <div
            key={slide.label}
            className={`${classPrefix}__slide-frame${
              isActive ? ` ${classPrefix}__slide-frame--active` : ''
            }`}
          >
            {shouldLoad ? (
              <img
                className={`${classPrefix}__slide${
                  isActive ? ` ${classPrefix}__slide--playing` : ''
                }`}
                src={slide.src}
                alt=""
                loading={index === 0 ? 'eager' : 'lazy'}
                fetchPriority={isActive ? 'high' : 'auto'}
                decoding={isActive ? 'sync' : 'async'}
                style={{ objectPosition: slide.position ?? 'center center' }}
                onError={handleError}
              />
            ) : null}
          </div>
        );
      })}
      <div className={`${classPrefix}__indicators`} aria-hidden="true">
        {slides.map((slide, index) => (
          <span
            key={slide.label}
            className={`${classPrefix}__dot${
              index === activeIndex ? ` ${classPrefix}__dot--active` : ''
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default HeroSlideshow;
